import React from 'react';
import { Icon } from './Icon';
import { cx } from './cx';

/* ---------- Бейджи ---------- */

// tone: neutral | success | warning | danger | info | accent
export function Badge({ tone = 'neutral', size = 'md', dot = false, pulse = false, icon, className, children }) {
    return (
        <span className={cx('ds-badge', `ds-badge--${tone}`, size === 'sm' && 'ds-badge--sm', className)}>
            {dot && <span className={cx('ds-badge__dot', pulse && 'is-pulse')} aria-hidden="true" />}
            {icon && <Icon name={icon} size={size === 'sm' ? 12 : 13} strokeWidth={2.2} />}
            {children}
        </span>
    );
}

// Единый словарь статусов админки: рассылки, сообщения, шаблоны, партнёры.
// Новые статусы с бэкенда добавлять сюда, а не красить на страницах.
export const STATUSES = {
    draft:     { tone: 'neutral', label: 'Черновик' },
    scheduled: { tone: 'info',    label: 'Запланирована' },
    pending:   { tone: 'warning', label: 'В очереди' },
    running:   { tone: 'warning', label: 'Идёт отправка', pulse: true },
    sent:      { tone: 'info',    label: 'Отправлено' },
    delivered: { tone: 'success', label: 'Доставлено' },
    read:      { tone: 'success', label: 'Прочитано' },
    done:      { tone: 'success', label: 'Завершена' },
    failed:    { tone: 'danger',  label: 'Ошибка' },
    error:     { tone: 'danger',  label: 'Ошибка' },
    cancelled: { tone: 'neutral', label: 'Отменена' },
    approved:  { tone: 'success', label: 'Одобрен' },
    rejected:  { tone: 'danger',  label: 'Отклонён' },
    review:    { tone: 'warning', label: 'На модерации' },
    active:    { tone: 'success', label: 'Активен' },
    disabled:  { tone: 'neutral', label: 'Отключён' },
};

export function StatusBadge({ status, label, size }) {
    const s = STATUSES[String(status || '').toLowerCase()] || { tone: 'neutral', label: status || '—' };
    return (
        <Badge tone={s.tone} size={size} dot pulse={s.pulse}>
            {label || s.label}
        </Badge>
    );
}

/* ---------- Карточка ---------- */

export function Card({ title, subtitle, actions, footer, flush = false, onClick, className, children, ...rest }) {
    const interactive = Boolean(onClick);
    return (
        <section
            className={cx('ds-card', interactive && 'ds-card--interactive', className)}
            onClick={onClick}
            {...rest}
        >
            {(title || actions) && (
                <header className="ds-card__head">
                    <div className="ds-card__titles">
                        {title && <h3 className="ds-h3">{title}</h3>}
                        {subtitle && <p className="ds-caption">{subtitle}</p>}
                    </div>
                    {actions && <div className="ds-btn-group">{actions}</div>}
                </header>
            )}
            <div className={cx('ds-card__body', flush && 'ds-card__body--flush')}>{children}</div>
            {footer && <footer className="ds-card__foot">{footer}</footer>}
        </section>
    );
}

/* ---------- Метрика ---------- */

// delta: число в процентах (+12.5 / -3); invertDelta — когда рост это плохо (ошибки, расход)
export function Stat({ label, value, unit, icon, delta, deltaLabel, invertDelta = false, hint, loading }) {
    const hasDelta = typeof delta === 'number' && !Number.isNaN(delta);
    const good = hasDelta && (invertDelta ? delta <= 0 : delta >= 0);
    return (
        <div className="ds-stat">
            <div className="ds-stat__top">
                <span className="ds-stat__label">{label}</span>
                {icon && (
                    <span className="ds-stat__icon">
                        <Icon name={icon} size={16} />
                    </span>
                )}
            </div>
            {loading ? (
                <span className="ds-skeleton" style={{ '--_h': '28px', '--_w': '60%' }} />
            ) : (
                <span className="ds-stat__value">
                    {value}
                    {unit && <span className="ds-stat__unit">{unit}</span>}
                </span>
            )}
            {(hasDelta || hint) && (
                <span className="ds-stat__foot">
                    {hasDelta && (
                        <span className={cx('ds-stat__delta', good ? 'ds-stat__delta--up' : 'ds-stat__delta--down')}>
                            <Icon name={delta >= 0 ? 'arrowUp' : 'arrowDown'} size={13} strokeWidth={2.4} />
                            {Math.abs(delta).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%
                        </span>
                    )}
                    {deltaLabel || hint}
                </span>
            )}
        </div>
    );
}

/* ---------- Аватар ---------- */

export function initials(name = '') {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

export function Avatar({ name, src, size = 32, square = false, className }) {
    return (
        <span
            className={cx('ds-avatar', square && 'ds-avatar--square', className)}
            style={{ '--_s': `${size}px` }}
            title={name}
            aria-label={name}
            role="img"
        >
            {src ? <img src={src} alt="" /> : initials(name)}
        </span>
    );
}

/* ---------- Раскладка ---------- */

export function PageHead({ title, subtitle, actions }) {
    return (
        <div className="ds-page-head">
            <div className="ds-page-head__titles">
                <h1 className="ds-h1">{title}</h1>
                {subtitle && <p className="ds-small">{subtitle}</p>}
            </div>
            {actions && <div className="ds-btn-group">{actions}</div>}
        </div>
    );
}

export function Toolbar({ start, end, className }) {
    return (
        <div className={cx('ds-toolbar', className)}>
            <div className="ds-toolbar__group">{start}</div>
            {end && <div className="ds-toolbar__group">{end}</div>}
        </div>
    );
}

export function Stack({ gap, className, style, children, as: Tag = 'div' }) {
    return (
        <Tag className={cx('ds-stack', className)} style={{ ...(gap != null && { '--_gap': `${gap}px` }), ...style }}>
            {children}
        </Tag>
    );
}

export function Row({ gap, className, style, children }) {
    return (
        <div className={cx('ds-row', className)} style={{ ...(gap != null && { '--_gap': `${gap}px` }), ...style }}>
            {children}
        </div>
    );
}

export function Grid({ min = 200, gap, className, children }) {
    return (
        <div className={cx('ds-grid', className)} style={{ '--_min': `${min}px`, ...(gap != null && { gap }) }}>
            {children}
        </div>
    );
}
